import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import FerdigBehanldeVedtakerContainer from "./FerdigBehandleVedtakerContainer";
import VedtakerList from "./VedtakerList";

import {
    handleChange as handleChangeAction,
    handleChangeAll as handleChangeAllAction,
} from "../../actions/handleChangeAction";
import { getIleggelseDetailByNummer } from "../../actions/klager/klageAction";
import {
    addKlageVedtak,
    deleteKlageVedtak,
    getKlageVedtaker,
    updateKlageVedtak,
} from "../../actions/klager/vedtakAction";
import { getVedtakskoder } from "../../actions/vedtakskoderAction";
import { overtredelsekodeForEditereBelop } from "../../constants/globals";
import { juridiskAll } from "../../constants/roles";
import { getVedtakskoderVmByIleggelsetyper } from "../../selectors/vedtakSelector";
import { authorized } from "../../utils/authorizationUtils";
import { onChangeValidation } from "../../utils/validationUtils";

function VedtakerContainer({ manglerInfo }) {
  const dispatch = useDispatch();
  const klageId = useSelector((state) =>
    state.klageDetail && state.klageDetail.id ? state.klageDetail.id : ""
  );
  const sak = useSelector((state) => state.sak);
  const vedtaker = useSelector((state) => state.vedtaker);
  const vedtakskoder = useSelector((state) =>
    getVedtakskoderVmByIleggelsetyper(state)
  );
  const klageVedtakerForm = useSelector(
    (state) => state.forms && state.forms.klageVedtakerForm
  );

  const [nyRadModus, setNyRadModus] = useState(false);
  const [showNyttDokumentSpinner] = useState(false);

  const rules = { vedtakskode: "required" };

  const getHasIkkeFerdigbehandletVedtaker = () => {
    const vedtakerArray = Object.values(vedtaker);
    for (const vedtak of vedtakerArray) {
      if (vedtak.vedtaksDato === null) return true;
    }
    return false;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dispatch(getVedtakskoder());
    if (klageId) {
      dispatch(getKlageVedtaker(klageId)).then((klagevedtaker) => {
        if (Array.isArray(klagevedtaker)) {
          const klageVedtakerForm = klagevedtaker.reduce((object, kv) => {
            object[kv.id] = {
              vedtakskode: kv.vedtakskodeId,
              nyttBelop: kv.nyttBelop,
              klagekode: kv.klagekode,
              validationError: {},
            };
            return object;
          }, {});
          dispatch(handleChangeAllAction("KLAGE_VEDTAKER_FORM", klageVedtakerForm));
        }
      });
    }
    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  const prevVedtakerRef = useRef(null);
  useEffect(() => {
    if (JSON.stringify(prevVedtakerRef.current) !== JSON.stringify(vedtaker)) {
      prevVedtakerRef.current = vedtaker;
      const klageVedtakerFormNew = Object.values(vedtaker).reduce((object, kv) => {
        object[kv.id] = {
          vedtakskode: kv.vedtakskodeId,
          nyttBelop: kv.nyttBelop,
          klagekode: kv.klagekode,
          validationError: {},
        };
        return object;
      }, {});
      dispatch(handleChangeAllAction("KLAGE_VEDTAKER_FORM", klageVedtakerFormNew));
      if (getHasIkkeFerdigbehandletVedtaker()) {
        window.onbeforeunload = () => "Minst ett vedtak er ikke ferdigbehandlet.";
      } else {
        window.onbeforeunload = null;
      }
    }
  }, [vedtaker]);

  const onChangeVedtakskode = async (e, vedtak) => {
    const klagevedtak = klageVedtakerForm[vedtak.id];
    onChangeValidation(e, klagevedtak, rules);
    const updatedKlagevedtak = {
      ...klagevedtak,
      [e.target.name]: e.target.value,
      oldVedtakskode: klagevedtak.vedtakskode,
      validationError: {},
    };
    dispatch(
      handleChangeAction("KLAGE_VEDTAKER_FORM", vedtak.id, updatedKlagevedtak)
    );
    getKlageVedtaker(klageId); // note: not dispatched, preserving original behaviour
    return updatedKlagevedtak;
  };

  const onConfirmKlagevedtakUpdate = async (vedtak, klagevedtakArg) => {
    const klagevedtak = klagevedtakArg || klageVedtakerForm[vedtak.id];
    const payload = {
      vedtakskodeId: Number(klagevedtak.vedtakskode),
      nyttBelop: klagevedtak.nyttBelop && Number(klagevedtak.nyttBelop),
    };
    const result = await dispatch(updateKlageVedtak(klageId, vedtak.id, payload));
    dispatch(getKlageVedtaker(klageId));
    return result ? true : false;
  };

  const onCancelVedtakskode = (vedtak) => {
    const klagevedtak = klageVedtakerForm[vedtak.id];
    dispatch(
      handleChangeAction("KLAGE_VEDTAKER_FORM", vedtak.id, {
        ...klagevedtak,
        vedtakskode: klagevedtak.oldVedtakskode,
        oldVedtakskode: null,
        validationError: {},
      })
    );
  };

  const onSubmitNyttBelop = async (e, vedtak) => {
    const klagevedtak = klageVedtakerForm[vedtak.id];
    const updatedKlagevedtak = {
      ...klagevedtak,
      [e.target.name]: e.target.value,
      oldNyttBelop: klagevedtak.nyttBelop,
      validationError: {},
    };
    dispatch(handleChangeAction("KLAGE_VEDTAKER_FORM", vedtak.id, updatedKlagevedtak));
    return updatedKlagevedtak;
  };

  const onCancelNyttBelop = async (vedtak) => {
    const klagevedtak = klageVedtakerForm[vedtak.id];
    dispatch(
      handleChangeAction("KLAGE_VEDTAKER_FORM", vedtak.id, {
        ...klagevedtak,
        nyttBelop: klagevedtak.oldNyttBelop,
        oldNyttBelop: null,
        validationError: {},
      })
    );
  };

  const onRemoveVedtak = async (e, vedtak) => {
    await dispatch(deleteKlageVedtak(klageId, vedtak.id));
    await dispatch(getKlageVedtaker(klageId));
  };

  const onAddNyVedtakRad = () => setNyRadModus(true);
  const onRemoveNyVedtakRad = () => setNyRadModus(false);

  const onSubmitNyVedtak = async (e) => {
    const ileggelsesnummer = e.target.value;
    const ileggelse = await dispatch(getIleggelseDetailByNummer(ileggelsesnummer));
    if (ileggelse && ileggelse.ileggelsesnummer) {
      const klagevedtak = await dispatch(addKlageVedtak(klageId, ileggelse)).catch(
        () => {
          setNyRadModus(false);
        }
      );
      if (klagevedtak && klagevedtak.id) {
        const klagevedtaker = await dispatch(getKlageVedtaker(klageId));
        if (Array.isArray(klagevedtaker)) {
          const vedtak = klagevedtaker.find((kv) => kv.id === klagevedtak.id);
          dispatch(
            handleChangeAction("KLAGE_VEDTAKER_FORM", klagevedtak.id, {
              vedtakskode: vedtak.vedtakskodeId,
              nyttBelop: vedtak.nyttBelop,
              validationError: {},
            })
          );
        }
      }
      setNyRadModus(false);
    }
  };

  const onlyForJuridisk = authorized(juridiskAll);
  const tableHeader = [
    "Ilegg.nr.",
    "Vedtak",
    "Vedtaksdato",
    "Ileggelsestype",
    "Gebyrdato",
    "Beløp",
    "Nytt beløp",
    "",
  ];
  const editNyttBelopKlageKoder = [14, 15, 16, 17, 43, 45];

  return (
    <div className="klageVedtak">
      <VedtakerList
        sak={sak}
        manglerInfo={manglerInfo}
        nyRadModus={nyRadModus}
        tableHeader={tableHeader}
        klageVedtakerForm={klageVedtakerForm}
        onChangeVedtakskode={onChangeVedtakskode}
        onCancelVedtakskode={onCancelVedtakskode}
        onSubmitIleggesesnummer={undefined}
        onSubmitNyttBelop={onSubmitNyttBelop}
        onConfirmKlagevedtakUpdate={onConfirmKlagevedtakUpdate}
        onCancelNyttBelop={onCancelNyttBelop}
        onAdd={onAddNyVedtakRad}
        onRemove={onRemoveVedtak}
        vedtaker={vedtaker}
        vedtakskoder={vedtakskoder}
        overtredelsekodeForEditereBelop={overtredelsekodeForEditereBelop}
        onSubmitNyVedtak={onSubmitNyVedtak}
        showFerdigbehandleVedtakSpinner={undefined}
        showNyttDokumentSpinner={showNyttDokumentSpinner}
        onRemoveNyVedtakRad={onRemoveNyVedtakRad}
        FerdigBehandleVedtaker={undefined}
        editNyttBelopKlageKoder={editNyttBelopKlageKoder}
      />
      {onlyForJuridisk && Object.keys(vedtaker).length > 0 && !manglerInfo && (
        <React.Fragment>
          <FerdigBehanldeVedtakerContainer />
        </React.Fragment>
      )}
    </div>
  );
}

VedtakerContainer.propTypes = {
  manglerInfo: PropTypes.bool,
};

export default VedtakerContainer;
